// Pixel art that was scaled up (and maybe JPEG-compressed): finds the real pixel size and reads it back as a 1-bit bitmap.
// An image is { w, h, lum } with lum a Uint8Array of luminance 0-255 (transparent counts as white).

const MAX_SIDE = 4000;

export const imageFromBlob = (blob) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale)), h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data;
    const lum = new Uint8Array(w * h);
    for (let i = 0; i < lum.length; i++) lum[i] = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
    resolve({ w, h, lum });
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Kuvan lataus epäonnistui')); };
  img.src = url;
});

export const otsuThreshold = (lum) => {
  const hist = new Array(256).fill(0);
  for (let i = 0; i < lum.length; i++) hist[lum[i]]++;
  let sum = 0;
  for (let t = 0; t < 256; t++) sum += t * hist[t];
  let wB = 0, sumB = 0, best = 128, bestVar = -1;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (!wB) continue;
    const wF = lum.length - wB;
    if (!wF) break;
    sumB += t * hist[t];
    const diff = sumB / wB - (sum - sumB) / wF;
    const between = wB * wF * diff * diff;
    if (between > bestVar) { bestVar = between; best = t; }
  }
  return best;
};

// profile[x] = how much the image changes between column x-1 and x, summed over all rows (or the same for rows).
const edgeProfiles = ({ w, h, lum }) => {
  const cols = new Float64Array(w), rows = new Float64Array(h);
  for (let y = 0; y < h; y++) {
    for (let x = 1; x < w; x++) {
      const d = Math.abs(lum[y * w + x] - lum[y * w + x - 1]);
      cols[x] += d;
      if (y > 0) rows[y] += Math.abs(lum[y * w + x] - lum[(y - 1) * w + x]);
    }
  }
  return { cols, rows };
};

const lineScore = (profile, mean, s, o) => {
  let total = 0, n = 0;
  for (let p = o; p < profile.length - 0.5; p += s) {
    const i = Math.round(p);
    if (i >= 1 && i < profile.length) { total += profile[i]; n++; }
  }
  return n ? total / n / mean : 0;
};

const bestOffset = (profile, mean, s, step) => {
  let best = { score: -1, o: 0 };
  for (let o = 0; o < s; o += step) {
    const score = lineScore(profile, mean, s, o);
    if (score > best.score) best = { score, o };
  }
  return best;
};

// Autocorrelation of an edge profile, normalised by its zero lag.
const autocorrelation = (profile, maxLag) => {
  const n = profile.length;
  let mean = 0;
  for (let i = 0; i < n; i++) mean += profile[i];
  mean /= n;
  const out = new Float64Array(maxLag + 1);
  for (let lag = 0; lag <= maxLag; lag++) {
    let total = 0;
    for (let i = 0; i + lag < n; i++) total += (profile[i] - mean) * (profile[i + lag] - mean);
    out[lag] = total / (n - lag);
  }
  const zero = out[0] || 1;
  return out.map(v => v / zero);
};

// Pixel edges repeat every `size` px. The edge profiles' autocorrelation gives the rough size (the first strong
// peak; multiples of the size peak too), then lattice positions offset + k * size are fitted around it to get
// the exact, possibly fractional, size and the offset.
export const detectPixelSize = (image) => {
  const { cols, rows } = edgeProfiles(image);
  const meanOf = (p) => p.reduce((a, b) => a + b, 0) / p.length || 1;
  const mc = meanOf(cols), mr = meanOf(rows);
  const maxLag = Math.min(60, Math.floor(Math.min(image.w, image.h) / 3));
  const acRows = autocorrelation(rows, maxLag);
  const ac = autocorrelation(cols, maxLag).map((v, i) => v + acRows[i]);
  let top = 0;
  for (let l = 2; l <= maxLag; l++) top = Math.max(top, ac[l]);
  let lag = 0;
  for (let l = 2; l < maxLag; l++) {
    if (ac[l] > ac[l - 1] && ac[l] >= ac[l + 1] && ac[l] >= 0.7 * top) { lag = l; break; }
  }
  if (!lag) lag = 8;
  const fit = (from, to, step) => {
    let found = null;
    for (let s = Math.max(2, from); s <= to + 1e-9; s += step) {
      const c = bestOffset(cols, mc, s, 0.1), r = bestOffset(rows, mr, s, 0.1);
      if (!found || c.score + r.score > found.score) found = { score: c.score + r.score, s, ox: c.o, oy: r.o };
    }
    return found;
  };
  let best = fit(lag - 0.5, lag + 0.5, 0.004);
  // The autocorrelation peak can be a multiple of the real size when thick strokes hide the single-pixel edges. If
  // a fraction of the size still finds edges (the half-way lines score well above the ~50% an empty lattice gets),
  // the smaller size is the real pixel.
  for (let changed = true; changed;) {
    changed = false;
    for (const k of [4, 3, 2]) {
      if (best.s / k < 2) continue;
      const smaller = fit(best.s / k * 0.97, best.s / k * 1.03, 0.002);
      if (smaller && smaller.score >= 0.7 * best.score) { best = smaller; changed = true; break; }
    }
  }
  // A whole-number scale is far more common than a fractional one, and then the offsets are whole pixels too.
  const whole = Math.round(best.s);
  if (Math.abs(best.s - whole) < 0.03) {
    const c = bestOffset(cols, mc, whole, 1), r = bestOffset(rows, mr, whole, 1);
    return { size: whole, ox: c.o, oy: r.o };
  }
  return { size: Math.round(best.s * 1000) / 1000, ox: Math.round(best.ox * 10) / 10, oy: Math.round(best.oy * 10) / 10 };
};

// Reads every lattice cell as ink when its middle is darker than the threshold.
export const sampleBitmap = ({ w, h, lum }, size, ox, oy, threshold) => {
  const nx = Math.max(0, Math.floor((w - ox) / size)), ny = Math.max(0, Math.floor((h - oy) / size));
  const bits = new Uint8Array(nx * ny);
  const half = Math.max(0.5, size * 0.25);
  for (let r = 0; r < ny; r++) {
    for (let c = 0; c < nx; c++) {
      const cx = ox + (c + 0.5) * size, cy = oy + (r + 0.5) * size;
      const x0 = Math.max(0, Math.floor(cx - half)), x1 = Math.min(w - 1, Math.max(x0, Math.ceil(cx + half) - 1));
      const y0 = Math.max(0, Math.floor(cy - half)), y1 = Math.min(h - 1, Math.max(y0, Math.ceil(cy + half) - 1));
      let total = 0, n = 0;
      for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) { total += lum[y * w + x]; n++; }
      bits[r * nx + c] = n && total / n < threshold ? 1 : 0;
    }
  }
  return { w: nx, h: ny, bits };
};

export const croppedBitmap = (b, x0, y0, x1, y1) => {
  const w = x1 - x0 + 1, h = y1 - y0 + 1;
  const bits = new Uint8Array(w * h);
  for (let r = 0; r < h; r++) for (let c = 0; c < w; c++) bits[r * w + c] = b.bits[(y0 + r) * b.w + x0 + c];
  return { w, h, bits };
};

// Drops empty rows and columns around the ink.
export const trimmedBitmap = (b) => {
  let minX = b.w, maxX = -1, minY = b.h, maxY = -1;
  for (let i = 0; i < b.bits.length; i++) {
    if (!b.bits[i]) continue;
    const x = i % b.w, y = Math.floor(i / b.w);
    minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y);
  }
  return maxX < 0 ? b : croppedBitmap(b, minX, minY, maxX, maxY);
};

const inkPerColumn = (b) => {
  const cols = new Float64Array(b.w), rows = new Float64Array(b.h);
  for (let i = 0; i < b.bits.length; i++) if (b.bits[i]) { cols[i % b.w]++; rows[Math.floor(i / b.w)]++; }
  return { cols, rows };
};

// Spacing of regularly placed sprites along one axis. In a perfect grid some line through each cell (its first
// row or column) is empty, so the smallest pitch whose lines carry almost no ink is the cell size; offset is where
// the cells start.
const gridAxis = (profile) => {
  const n = profile.length;
  let total = 0;
  for (let i = 0; i < n; i++) total += profile[i];
  if (!total) return null;
  for (let pitch = 4; pitch <= Math.floor(n / 3); pitch++) {
    let best = Infinity, offset = 0;
    for (let o = 0; o < pitch; o++) {
      let ink = 0;
      for (let x = o; x < n; x += pitch) ink += profile[x];
      if (ink < best) { best = ink; offset = o; }
    }
    if (best <= 0.01 * total) return { pitch, offset };
  }
  return null;
};

// { cw, ch, ox, oy } for a sheet whose sprites sit on a regular grid, or null when there is no such pattern.
export const detectSpriteGrid = (b) => {
  const { cols, rows } = inkPerColumn(b);
  const x = gridAxis(cols), y = gridAxis(rows);
  return x && y ? { cw: x.pitch, ch: y.pitch, ox: x.offset, oy: y.offset } : null;
};

// Number of whole cells that fit.
export const gridCounts = (b, { cw, ch, ox, oy }) => ({
  cols: cw >= 2 ? Math.max(0, Math.floor((b.w - ox) / cw)) : 0,
  rows: ch >= 2 ? Math.max(0, Math.floor((b.h - oy) / ch)) : 0,
});

// One sprite per whole grid cell, trimmed to its ink; empty and tiny cells are dropped.
export const gridSprites = (b, grid, minInk = 4) => {
  const { cw, ch, ox, oy } = grid;
  const { cols, rows } = gridCounts(b, grid);
  const out = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = ox + c * cw, y = oy + r * ch;
      const sprite = trimmedBitmap(croppedBitmap(b, x, y, x + cw - 1, y + ch - 1));
      if (sprite.bits.reduce((n, v) => n + v, 0) >= minInk) out.push(sprite);
    }
  }
  return out;
};
